import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n";
import { AuthProvider } from "@/auth";
import Navbar from "@/components/Navbar";
import PageBackBar from "@/components/PageBackBar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Journey from "@/pages/Journey";
import Countries from "@/pages/Countries";
import Calculator from "@/pages/Calculator";
import Recipes from "@/pages/Recipes";
import AddRecipe from "@/pages/AddRecipe";
import EditRecipe from "@/pages/EditRecipe";
import Assistant from "@/pages/Assistant";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import MyRecipes from "@/pages/MyRecipes";
import Academy from "@/pages/Academy";
import Encyclopedia from "@/pages/Encyclopedia";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BlogEditor from "@/pages/BlogEditor";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminPanel from "@/pages/AdminPanel";
import Challenge from "@/pages/Challenge";
import Achievements from "@/pages/Achievements";
import Community from "@/pages/Community";
import MyCafe from "@/pages/MyCafe";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import RequireAuth from "@/components/RequireAuth";
import { useSEO } from "@/hooks/useSEO";
import { useActivityTracker } from "@/hooks/useActivityTracker";

function AppRouter() {
  const location = useLocation();
  useSEO();
  useActivityTracker();
  // Detect OAuth callback in URL fragment (must run synchronously, NOT in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <Navbar />
      <PageBackBar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/journey" element={<Journey />} />
          <Route path="/academy" element={<RequireAuth><Academy /></RequireAuth>} />
          <Route path="/encyclopedia" element={<Encyclopedia />} />
          <Route path="/countries" element={<Countries />} />
          <Route path="/calculator" element={<RequireAuth><Calculator /></RequireAuth>} />
          <Route path="/challenge" element={<Challenge />} />
          <Route path="/achievements" element={<Achievements />} />
          <Route path="/community" element={<Community />} />
          <Route path="/my-cafe" element={<RequireAuth><MyCafe /></RequireAuth>} />
          <Route path="/recipes" element={<RequireAuth><Recipes /></RequireAuth>} />
          <Route path="/recipes/new" element={<RequireAuth><AddRecipe /></RequireAuth>} />
          <Route path="/recipes/:id/edit" element={<RequireAuth><EditRecipe /></RequireAuth>} />
          <Route path="/assistant" element={<Assistant />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/new" element={<RequireAuth><BlogEditor /></RequireAuth>} />
          <Route path="/blog/:id/edit" element={<RequireAuth><BlogEditor /></RequireAuth>} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin/testimonials" element={<AdminTestimonials />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/mine" element={<MyRecipes />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <PWAInstallPrompt />
          <WhatsAppFloat />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
